import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { 
  HiOutlineX, 
  HiOutlineCheck, 
  HiOutlineExclamation 
} from 'react-icons/hi';
import { updateUserProfile } from '../../store/slices/authSlice';
import { sendProfileUpdateNotification } from '../../emailjs/emailService';
import './ProfileEditForms.scss';

const EmergencyContactForm = ({ isOpen, onClose, userData }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    emergencyContact: userData.emergencyContact || '',
    emergencyRelation: userData.emergencyRelation || '',
    emergencyNumber: userData.emergencyNumber || '',
    emergencyContactAddress: userData.emergencyContactAddress || '',
    
    secondaryEmergencyContact: userData.secondaryEmergencyContact || '',
    secondaryEmergencyRelation: userData.secondaryEmergencyRelation || '',
    secondaryEmergencyNumber: userData.secondaryEmergencyNumber || '',
    secondaryEmergencyAddress: userData.secondaryEmergencyAddress || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add function to handle modal overlay click
  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Only include fields that have actually changed
      const updatedData = Object.keys(formData).reduce((acc, key) => {
        if (formData[key] !== userData[key]) {
          acc[key] = formData[key];
        }
        return acc;
      }, {});

      // Only dispatch if there are changes
      if (Object.keys(updatedData).length > 0) {
        await dispatch(updateUserProfile({
          email: userData.email,
          profileData: updatedData
        })).unwrap();
        
        // Send profile update notification email
        try {
          await sendProfileUpdateNotification(
            userData,
            'emergency_contact',
            updatedData
          );
          console.log('Emergency contact update notification sent successfully');
        } catch (emailError) {
          console.error('Failed to send emergency contact update notification:', emailError);
          // Don't fail the whole operation if email fails
        }
        
        setSuccess('Emergency contacts updated successfully!');
        // Close the modal after a short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSuccess('No changes detected');
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Error updating emergency contacts:', err);
      setError(err.message || 'Failed to update emergency contacts. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="profile-edit-modal">
      <div className="modal-overlay" onClick={handleOverlayClick}></div>
      <div className="modal-container">
        <div className="modal-header">
          <h2>Edit Emergency Contacts</h2>
          <button className="close-button" onClick={onClose}>
            <HiOutlineX />
          </button>
        </div>

        {error && (
          <div className="alert error">
            <HiOutlineExclamation />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert success">
            <HiOutlineCheck />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="edit-form">
          <div className="form-section">
            <h3>Primary Contact</h3>
            
            <div className="form-group">
              <label htmlFor="emergencyContact">Full Name</label>
              <input
                type="text"
                id="emergencyContact"
                name="emergencyContact"
                value={formData.emergencyContact}
                onChange={handleChange}
                placeholder="Enter full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="emergencyRelation">Relation</label>
              <input
                type="text"
                id="emergencyRelation"
                name="emergencyRelation"
                value={formData.emergencyRelation}
                onChange={handleChange}
                placeholder="Enter relation (e.g., Parent, Spouse, Sibling)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="emergencyNumber">Phone Number</label>
              <input
                type="tel"
                id="emergencyNumber"
                name="emergencyNumber"
                value={formData.emergencyNumber}
                onChange={handleChange}
                placeholder="Enter phone number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="emergencyContactAddress">Address</label>
              <textarea
                id="emergencyContactAddress"
                name="emergencyContactAddress"
                value={formData.emergencyContactAddress}
                onChange={handleChange}
                placeholder="Enter address"
                rows="3"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Secondary Contact</h3>
            
            <div className="form-group">
              <label htmlFor="secondaryEmergencyContact">Full Name</label>
              <input
                type="text"
                id="secondaryEmergencyContact"
                name="secondaryEmergencyContact"
                value={formData.secondaryEmergencyContact}
                onChange={handleChange}
                placeholder="Enter full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="secondaryEmergencyRelation">Relation</label>
              <input
                type="text"
                id="secondaryEmergencyRelation"
                name="secondaryEmergencyRelation"
                value={formData.secondaryEmergencyRelation}
                onChange={handleChange}
                placeholder="Enter relation (e.g., Parent, Spouse, Sibling)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="secondaryEmergencyNumber">Phone Number</label>
              <input
                type="tel"
                id="secondaryEmergencyNumber"
                name="secondaryEmergencyNumber"
                value={formData.secondaryEmergencyNumber}
                onChange={handleChange}
                placeholder="Enter phone number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="secondaryEmergencyAddress">Address</label>
              <textarea
                id="secondaryEmergencyAddress"
                name="secondaryEmergencyAddress"
                value={formData.secondaryEmergencyAddress}
                onChange={handleChange}
                placeholder="Enter address"
                rows="3"
              />
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmergencyContactForm; 